import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './AdminUI.css'

export const formatRWF = (value) =>
  `RWF ${Math.round(Number(value) || 0).toLocaleString('en-US')}`

export const formatDate = (value, withTime = false) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  })
}

export const timeAgo = (value) => {
  if (!value) return ''
  const diff = Date.now() - new Date(value).getTime()
  if (Number.isNaN(diff)) return ''
  const minutes = Math.round(diff / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.round(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.round(months / 12)}y ago`
}

export const formatOrderId = (id) => {
  if (!id) return '—'
  const value = String(id)
  if (value.startsWith('FT-')) return value
  if (/^[a-f0-9]{24}$/i.test(value)) return value.slice(-8).toUpperCase()
  return value.toUpperCase()
}

export const getInitials = (name) => {
  if (!name) return 'U'
  const parts = String(name).trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return String(name).substring(0, 2).toUpperCase()
}

export const capitalize = (value) =>
  value ? String(value).charAt(0).toUpperCase() + String(value).slice(1) : ''

export function downloadCsv(filename, rows) {
  const escape = (cell) => {
    const text = cell === null || cell === undefined ? '' : String(cell)
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
  }
  const csv = rows.map((row) => row.map(escape).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export function usePagination(items, initialSize = 10, resetKey = '') {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialSize)
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize))

  useEffect(() => {
    setPage(1)
  }, [resetKey, pageSize])

  useEffect(() => {
    if (page > pageCount) setPage(pageCount)
  }, [page, pageCount])

  const pageItems = useMemo(
    () => items.slice((page - 1) * pageSize, page * pageSize),
    [items, page, pageSize]
  )

  return {
    page,
    setPage,
    pageSize,
    setPageSize,
    pageCount,
    pageItems,
    total: items.length,
    from: items.length ? (page - 1) * pageSize + 1 : 0,
    to: Math.min(page * pageSize, items.length),
  }
}

function useOverlay(open, onClose) {
  useEffect(() => {
    if (!open) return undefined
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (event) => {
      if (event.key === 'Escape') onClose?.()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previous
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])
}

export function StatTiles({ items, active, onSelect }) {
  return (
    <div className="ax-stats">
      {items.map((item) => {
        const clickable = Boolean(onSelect && item.filter !== undefined)
        const Tag = clickable ? 'button' : 'div'
        return (
          <Tag
            key={item.id}
            type={clickable ? 'button' : undefined}
            className={`ax-stat ${clickable ? 'is-clickable' : ''} ${
              clickable && active === item.filter ? 'is-active' : ''
            }`}
            onClick={clickable ? () => onSelect(item.filter) : undefined}
          >
            <span className={`ax-stat__icon ax-tone--${item.tone || 'blue'}`} aria-hidden="true">
              <i className={`fas ${item.icon}`} />
            </span>
            <span className="ax-stat__body">
              <small>{item.label}</small>
              <strong>{item.value}</strong>
              {item.hint ? <em>{item.hint}</em> : null}
            </span>
          </Tag>
        )
      })}
    </div>
  )
}

export function SearchInput({ value, onChange, placeholder = 'Search…', label = 'Search' }) {
  return (
    <div className="ax-search">
      <i className="fas fa-search" aria-hidden="true" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
      />
      {value ? (
        <button type="button" className="ax-search__clear" onClick={() => onChange('')} aria-label="Clear search">
          <i className="fas fa-times" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  )
}

export function Tabs({ options, value, onChange, label = 'Filter' }) {
  return (
    <div className="ax-tabs" role="tablist" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          role="tab"
          aria-selected={value === option.id}
          className={`ax-tabs__tab ${value === option.id ? 'is-active' : ''}`}
          onClick={() => onChange(option.id)}
        >
          {option.label}
          {option.count !== undefined ? <span className="ax-tabs__count">{option.count}</span> : null}
        </button>
      ))}
    </div>
  )
}

export function SelectFilter({ value, onChange, options, icon, label }) {
  return (
    <label className="ax-select">
      {icon ? <i className={`fas ${icon}`} aria-hidden="true" /> : null}
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <i className="fas fa-chevron-down ax-select__caret" aria-hidden="true" />
    </label>
  )
}

export function Button({ variant = 'default', icon, children, loading, className = '', ...props }) {
  return (
    <button type="button" className={`ax-btn ax-btn--${variant} ${className}`} {...props}>
      {loading ? (
        <i className="fas fa-spinner fa-spin" aria-hidden="true" />
      ) : icon ? (
        <i className={`fas ${icon}`} aria-hidden="true" />
      ) : null}
      {children ? <span>{children}</span> : null}
    </button>
  )
}

export function IconButton({ icon, label, tone = 'default', className = '', ...props }) {
  return (
    <button
      type="button"
      className={`ax-icon-btn ax-icon-btn--${tone} ${className}`}
      aria-label={label}
      title={label}
      {...props}
    >
      <i className={`fas ${icon}`} aria-hidden="true" />
    </button>
  )
}

export function Pill({ tone = 'gray', children, dot = true }) {
  return <span className={`ax-pill ax-pill--${tone} ${dot ? 'has-dot' : ''}`}>{children}</span>
}

export function Avatar({ name, size = 'md', tone }) {
  return (
    <span className={`ax-avatar ax-avatar--${size} ${tone ? `ax-avatar--${tone}` : ''}`} aria-hidden="true">
      {getInitials(name)}
    </span>
  )
}

export function Thumb({ src, size = 'md' }) {
  const [failed, setFailed] = useState(false)
  return (
    <span className={`ax-thumb ax-thumb--${size}`}>
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
      ) : (
        <i className="fas fa-image" aria-hidden="true" />
      )}
    </span>
  )
}

export function Meter({ value, tone = 'blue' }) {
  return (
    <span className="ax-meter" aria-hidden="true">
      <span className={`ax-meter__fill ax-meter__fill--${tone}`} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </span>
  )
}

export function Banner({ tone = 'error', children, onClose }) {
  if (!children) return null
  return (
    <div className={`ax-banner ax-banner--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <i
        className={`fas ${tone === 'error' ? 'fa-circle-exclamation' : tone === 'warning' ? 'fa-triangle-exclamation' : 'fa-circle-info'}`}
        aria-hidden="true"
      />
      <span>{children}</span>
      {onClose ? (
        <button type="button" onClick={onClose} aria-label="Dismiss">
          <i className="fas fa-times" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  )
}

export function Toast({ message }) {
  if (!message) return null
  return createPortal(
    <div className="ax-toast" role="status">
      <i className="fas fa-circle-check" aria-hidden="true" />
      <span>{message}</span>
    </div>,
    document.body
  )
}

export function EmptyState({ icon = 'fa-inbox', title, text, action }) {
  return (
    <div className="ax-empty">
      <span className="ax-empty__icon" aria-hidden="true">
        <i className={`fas ${icon}`} />
      </span>
      <h3>{title}</h3>
      {text ? <p>{text}</p> : null}
      {action || null}
    </div>
  )
}

export function SkeletonRows({ rows = 6, cols = 5 }) {
  return (
    <div className="ax-skeleton" aria-hidden="true">
      {Array.from({ length: rows }, (_, r) => (
        <div className="ax-skeleton__row" key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <span key={c} style={{ width: c === 0 ? '28%' : `${12 + ((r + c) % 3) * 4}%` }} />
          ))}
        </div>
      ))}
    </div>
  )
}

export function Pagination({ pager, label = 'items' }) {
  const { page, pageCount, setPage, from, to, total, pageSize, setPageSize } = pager
  if (!total) return null
  const pages = []
  for (let i = 1; i <= pageCount; i += 1) {
    if (i === 1 || i === pageCount || Math.abs(i - page) <= 1) pages.push(i)
    else if (pages[pages.length - 1] !== '…') pages.push('…')
  }
  return (
    <div className="ax-pager">
      <span className="ax-pager__info">
        Showing <strong>{from}</strong>–<strong>{to}</strong> of <strong>{total}</strong> {label}
      </span>
      <div className="ax-pager__controls">
        <label className="ax-pager__size">
          Rows
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} aria-label="Rows per page">
            {[10, 25, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="ax-pager__btn" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Previous page">
          <i className="fas fa-chevron-left" aria-hidden="true" />
        </button>
        {pages.map((p, i) =>
          p === '…' ? (
            <span key={`gap-${i}`} className="ax-pager__gap">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={`ax-pager__btn ${p === page ? 'is-active' : ''}`}
              onClick={() => setPage(p)}
              aria-current={p === page ? 'page' : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          className="ax-pager__btn"
          disabled={page >= pageCount}
          onClick={() => setPage(page + 1)}
          aria-label="Next page"
        >
          <i className="fas fa-chevron-right" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}

export function Drawer({ open, onClose, title, subtitle, headerExtra, children, footer, width = 560 }) {
  useOverlay(open, onClose)
  if (!open) return null
  return createPortal(
    <div className="ax-overlay ax-overlay--drawer" onMouseDown={onClose} role="presentation">
      <aside
        className="ax-drawer"
        style={{ '--ax-drawer-w': `${width}px` }}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="ax-drawer__head">
          <div className="ax-drawer__titles">
            <h2>{title}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <div className="ax-drawer__extra">
            {headerExtra}
            <IconButton icon="fa-times" label="Close" onClick={onClose} />
          </div>
        </header>
        <div className="ax-drawer__body">{children}</div>
        {footer ? <footer className="ax-drawer__foot">{footer}</footer> : null}
      </aside>
    </div>,
    document.body
  )
}

export function Modal({ open, onClose, title, subtitle, icon, media, children, footer, size = 'md', as = 'div', onSubmit, className = '' }) {
  useOverlay(open, onClose)
  if (!open) return null
  const Body = as
  return createPortal(
    <div className="ax-overlay ax-overlay--modal" onMouseDown={onClose} role="presentation">
      <Body
        className={`ax-modal ax-modal--${size} ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
      >
        <header className="ax-modal__head">
          {media ? (
            <span className="ax-modal__media">{media}</span>
          ) : icon ? (
            <span className="ax-modal__icon" aria-hidden="true">
              <i className={`fas ${icon}`} />
            </span>
          ) : null}
          <div className="ax-modal__titles">
            <h2>{title}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <IconButton icon="fa-times" label="Close" onClick={onClose} />
        </header>
        <div className="ax-modal__body">{children}</div>
        {footer ? <footer className="ax-modal__foot">{footer}</footer> : null}
      </Body>
    </div>,
    document.body
  )
}

export function ConfirmDialog({
  open,
  busy = false,
  tone = 'danger',
  icon = 'fa-trash-can',
  title,
  message,
  item,
  points,
  confirmLabel = 'Delete',
  confirmIcon,
  busyLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
}) {
  const cancelRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    // Capture phase so Escape closes only this dialog, not a drawer underneath it.
    const onKey = (event) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onCancel?.()
    }
    document.addEventListener('keydown', onKey, true)
    cancelRef.current?.focus()
    return () => {
      document.body.style.overflow = previous
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open, onCancel])

  if (!open) return null

  return createPortal(
    <div className="ax-overlay ax-overlay--modal ax-overlay--confirm" onMouseDown={onCancel} role="presentation">
      <div
        className={`ax-confirm ax-confirm--${tone}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="ax-confirm-title"
        aria-describedby={message ? 'ax-confirm-message' : undefined}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="ax-confirm__close" aria-label="Close" onClick={onCancel} disabled={busy}>
          <i className="fas fa-times" aria-hidden="true" />
        </button>
        <span className="ax-confirm__icon" aria-hidden="true">
          <i className={`fas ${icon}`} />
        </span>
        <h2 id="ax-confirm-title">{title}</h2>
        {message ? <p id="ax-confirm-message">{message}</p> : null}

        {item ? (
          <div className="ax-confirm__item">
            {item.media ? <span className="ax-confirm__media">{item.media}</span> : null}
            <span className="ax-confirm__item-text">
              <strong>{item.title}</strong>
              {item.meta ? <small>{item.meta}</small> : null}
            </span>
          </div>
        ) : null}

        {points?.length ? (
          <ul className="ax-confirm__points">
            {points.map((point) => (
              <li key={point}>
                <i className="fas fa-circle-exclamation" aria-hidden="true" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="ax-confirm__actions">
          <button ref={cancelRef} type="button" className="ax-btn" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className="ax-btn ax-confirm__go"
            onClick={onConfirm}
            disabled={busy}
          >
            <i className={`fas ${busy ? 'fa-spinner fa-spin' : confirmIcon || icon}`} aria-hidden="true" />
            <span>{busy ? busyLabel || 'Working…' : confirmLabel}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

/**
 * const [confirm, confirmDialog] = useConfirm()
 * await confirm({ title, message, item, onConfirm: async () => {...} })
 * Resolves true once confirmed (after onConfirm settles), false when cancelled.
 */
export function useConfirm() {
  const [request, setRequest] = useState(null)
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)

  const confirm = useCallback(
    (options) =>
      new Promise((resolve) => {
        setRequest({ options, resolve })
      }),
    []
  )

  const cancel = useCallback(() => {
    if (busyRef.current) return
    setRequest((current) => {
      current?.resolve(false)
      return null
    })
  }, [])

  const accept = async () => {
    if (!request || busyRef.current) return
    const { options, resolve } = request
    if (options.onConfirm) {
      busyRef.current = true
      setBusy(true)
      try {
        await options.onConfirm()
      } finally {
        busyRef.current = false
        setBusy(false)
      }
    }
    resolve(true)
    setRequest(null)
  }

  const { onConfirm: _ignored, ...dialogProps } = request?.options || {}
  const dialog = (
    <ConfirmDialog open={Boolean(request)} busy={busy} {...dialogProps} onCancel={cancel} onConfirm={accept} />
  )

  return [confirm, dialog]
}

export function RowActions({ title, subtitle, media, actions, label = 'More actions' }) {
  const [open, setOpen] = useState(false)
  const visible = actions.filter(Boolean)
  const regular = visible.filter((a) => a.tone !== 'danger')
  const danger = visible.filter((a) => a.tone === 'danger')

  const run = (action) => {
    if (action.disabled) return
    setOpen(false)
    action.onClick()
  }

  const renderAction = (action) => (
    <li key={action.id || action.label}>
      <button
        type="button"
        className={`ax-action-item ax-action-item--${action.tone || 'default'}`}
        disabled={action.disabled}
        onClick={() => run(action)}
      >
        <span className="ax-action-item__icon" aria-hidden="true">
          <i className={`fas ${action.icon}`} />
        </span>
        <span className="ax-action-item__text">
          <strong>{action.label}</strong>
          {action.description ? <small>{action.description}</small> : null}
        </span>
        <i className="fas fa-chevron-right ax-action-item__go" aria-hidden="true" />
      </button>
    </li>
  )

  return (
    <>
      <button
        type="button"
        className={`ax-kebab ${open ? 'is-open' : ''}`}
        aria-label={label}
        title={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation()
          setOpen(true)
        }}
      >
        <i className="fas fa-ellipsis-vertical" aria-hidden="true" />
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="sm"
        className="ax-modal--actions"
        media={media}
        icon="fa-ellipsis"
        title={title}
        subtitle={subtitle}
      >
        <p className="ax-action-list__label">Choose an action</p>
        <ul className="ax-action-list">{regular.map(renderAction)}</ul>
        {danger.length ? (
          <>
            <p className="ax-action-list__label ax-action-list__label--danger">Danger zone</p>
            <ul className="ax-action-list">{danger.map(renderAction)}</ul>
          </>
        ) : null}
      </Modal>
    </>
  )
}

export function Field({ label, hint, required, children, span = 1, htmlFor }) {
  return (
    <div className={`ax-field ax-field--span-${span}`}>
      {label ? (
        <label className="ax-field__label" htmlFor={htmlFor}>
          {label}
          {required ? <span className="ax-field__req">*</span> : null}
        </label>
      ) : null}
      {children}
      {hint ? <small className="ax-field__hint">{hint}</small> : null}
    </div>
  )
}

export function FormSection({ title, text, children }) {
  return (
    <section className="ax-form-section">
      <div className="ax-form-section__head">
        <h3>{title}</h3>
        {text ? <p>{text}</p> : null}
      </div>
      <div className="ax-form-grid">{children}</div>
    </section>
  )
}

export function DetailList({ rows }) {
  return (
    <dl className="ax-dl">
      {rows
        .filter((row) => row && row.value !== undefined)
        .map((row) => (
          <div className="ax-dl__row" key={row.label}>
            <dt>{row.label}</dt>
            <dd className={row.mono ? 'is-mono' : ''}>{row.value || '—'}</dd>
          </div>
        ))}
    </dl>
  )
}

export function useFlash(timeout = 2800) {
  const [message, setMessage] = useState('')
  useEffect(() => {
    if (!message) return undefined
    const t = setTimeout(() => setMessage(''), timeout)
    return () => clearTimeout(t)
  }, [message, timeout])
  return [message, setMessage]
}
